import { z } from 'zod';

export enum TrackEscalationMethod {
  TEAMS = 'TEAMS',
  SLACK = 'SLACK',
  FRONT = 'FRONT',
  INTERNAL_CHAT = 'INTERNAL_CHAT',
  EMAIL = 'EMAIL',
  DO_NOTHING = 'DO_NOTHING',
}

export enum TrackEscalationRecipientType {
  RESPONSIBLE_PARTY = 'RESPONSIBLE_PARTY',
  EMAIL = 'EMAIL',
}

export const TRACK_ESCALATE_SOP_TAG = 'track - escalate';

export const TRACK_ESCALATE_SOP_INSTRUCTIONS_PROMPT_VALUE_KEY =
  'escalationInstructions';

export const TRACK_ESCALATION_RULES_PROMPT_VALUE_KEY = 'trackEscalationRules';

export const DEFAULT_TRACK_ESCALATION_RULE_ID =
  'default-email-responsible-party';

export const DEFAULT_TRACK_ESCALATION_EMAIL_INSTRUCTIONS = [
  'Subject: Order # <orderNumber> <short description of issue, 3-4 words>',
  'Body: a succinct explanation of the issue',
].join('\n');

export const TRACK_ESCALATION_CHANNEL_LABELS: Record<
  TrackEscalationMethod,
  string
> = {
  [TrackEscalationMethod.TEAMS]: 'Teams',
  [TrackEscalationMethod.SLACK]: 'Slack',
  [TrackEscalationMethod.FRONT]: 'Front',
  [TrackEscalationMethod.INTERNAL_CHAT]: 'Internal chat',
  [TrackEscalationMethod.EMAIL]: 'Email',
  [TrackEscalationMethod.DO_NOTHING]: 'Do nothing',
};

export const TRACK_ESCALATION_METHOD_LABELS = TRACK_ESCALATION_CHANNEL_LABELS;

export const TrackEscalationChannelSchema = z
  .object({
    type: z.nativeEnum(TrackEscalationMethod),
    instructions: z.string().optional(),
    template: z.string().optional(),
    recipient: z.string().nullable().optional(),
    recipientType: z.nativeEnum(TrackEscalationRecipientType).optional(),
    recipientEmail: z.string().nullable().optional(),
    toolParams: z
      .object({
        webhookUrl: z.string().optional(),
      })
      .nullable()
      .optional(),
    overrideTemplate: z.boolean().optional(),
  })
  .transform((channel) => ({
    type: channel.type,
    instructions: channel.instructions ?? channel.template ?? '',
    ...specificEmailRecipient(channel),
  }));

const TrackEscalationRuleCurrentSchema = z.object({
  id: z.string().min(1),
  scenario: z.string(),
  channels: z.array(TrackEscalationChannelSchema),
});

const TrackEscalationRuleLegacySchema = z.object({
  id: z.string().min(1),
  scenario: z.string(),
  methods: z.array(z.nativeEnum(TrackEscalationMethod)),
  recipientType: z.nativeEnum(TrackEscalationRecipientType).optional(),
  recipientEmail: z.string().nullable().optional(),
  template: z.string().optional(),
});

export type TrackEscalationChannel = z.infer<
  typeof TrackEscalationChannelSchema
>;

export function createEmptyTrackEscalationChannel(
  type: TrackEscalationMethod
): TrackEscalationChannel {
  return {
    type,
    instructions: '',
  };
}

function migrateLegacyTrackEscalationRule(
  legacy: z.infer<typeof TrackEscalationRuleLegacySchema>
): z.infer<typeof TrackEscalationRuleCurrentSchema> {
  const emailRecipient = specificEmailRecipient({
    type: TrackEscalationMethod.EMAIL,
    recipientType: legacy.recipientType,
    recipientEmail: legacy.recipientEmail,
  });
  return {
    id: legacy.id,
    scenario: legacy.scenario,
    channels: legacy.methods.map((type) => ({
      type,
      instructions: legacy.template ?? '',
      ...(type === TrackEscalationMethod.EMAIL ? emailRecipient ?? {} : {}),
    })),
  };
}

// NOTE(jonty): Accept a method-list-plus-template shape so older drafts still parse.
export const TrackEscalationRuleSchema = z
  .union([TrackEscalationRuleCurrentSchema, TrackEscalationRuleLegacySchema])
  .transform((rule) =>
    'channels' in rule ? rule : migrateLegacyTrackEscalationRule(rule)
  );

export type TrackEscalationRule = z.infer<typeof TrackEscalationRuleSchema>;

export function createEmptyTrackEscalationRule(): TrackEscalationRule {
  return {
    id: createTrackEscalationRuleId(),
    scenario: '',
    channels: [],
  };
}

export function defaultTrackEscalationRule(): TrackEscalationRule {
  return {
    id: DEFAULT_TRACK_ESCALATION_RULE_ID,
    scenario: '',
    channels: [
      {
        type: TrackEscalationMethod.EMAIL,
        instructions: DEFAULT_TRACK_ESCALATION_EMAIL_INSTRUCTIONS,
      },
    ],
  };
}

export function rulesOrEmailDefault(
  rules: TrackEscalationRule[]
): TrackEscalationRule[] {
  return rules.some(hasEscalationContent)
    ? rules
    : [defaultTrackEscalationRule()];
}

export function parseTrackEscalationRules(
  rawRules: unknown[] | null | undefined
): TrackEscalationRule[] {
  return (rawRules ?? []).flatMap((rule) => {
    const parsed = TrackEscalationRuleSchema.safeParse(rule);
    return parsed.success ? [parsed.data] : [];
  });
}

export function serializeTrackEscalationRulesForSop(
  rules: TrackEscalationRule[]
): string {
  return JSON.stringify(rules);
}

export function parseTrackEscalationRulesFromSopValues(
  values?: Record<string, string> | null
): TrackEscalationRule[] {
  const raw = values?.[TRACK_ESCALATION_RULES_PROMPT_VALUE_KEY];
  if (!raw?.trim()) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return parseTrackEscalationRules(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
}

export function buildTrackEscalateSopPrompt(
  rules: TrackEscalationRule[]
): string {
  const configuredRules = orderSpecificRulesBeforeFallback(
    rulesOrEmailDefault(rules.filter(hasEscalationContent))
  );

  const ruleSections = configuredRules.map((rule, index) =>
    formatEscalationRuleSection(rule, index + 1)
  );

  return [
    '# Track escalation routing',
    '',
    'These instructions are generated from Track notification rules.',
    'Evaluate the following scenarios in order. Apply only the first matching scenario.',
    'If none match, do nothing.',
    '',
    ...ruleSections,
  ].join('\n');
}

function hasEscalationContent(rule: TrackEscalationRule): boolean {
  return rule.scenario.trim().length > 0 || rule.channels.length > 0;
}

function orderSpecificRulesBeforeFallback(
  rules: TrackEscalationRule[]
): TrackEscalationRule[] {
  return [
    ...rules.filter((rule) => rule.scenario.trim().length > 0),
    ...rules.filter((rule) => rule.scenario.trim().length === 0),
  ];
}

function formatEscalationRuleSection(
  rule: TrackEscalationRule,
  position: number
): string {
  const title = rule.scenario.trim() || 'All other loads';
  const lines = [`## ${position}. ${title}`];

  if (
    rule.channels.some(
      (channel) => channel.type === TrackEscalationMethod.DO_NOTHING
    )
  ) {
    lines.push('- Action: Do nothing. Do not notify anyone.');
    return `${lines.join('\n')}\n`;
  }

  const channels =
    rule.channels.length > 0
      ? rule.channels.map(
          (channel) => TRACK_ESCALATION_CHANNEL_LABELS[channel.type]
        )
      : ['unspecified'];
  lines.push(`- Channels: ${channels.join(', ')}`);

  for (const channel of rule.channels) {
    lines.push(...formatEscalationChannelLines(channel));
  }

  return `${lines.join('\n')}\n`;
}

function formatEscalationChannelLines(
  channel: TrackEscalationChannel
): string[] {
  const label = TRACK_ESCALATION_CHANNEL_LABELS[channel.type];
  const lines: string[] = [];
  if (channel.type === TrackEscalationMethod.EMAIL) {
    lines.push(`- ${label} recipient: ${emailRecipientLabel(channel)}`);
  }
  const instructions = channel.instructions?.trim();
  if (instructions) {
    lines.push(`- ${label} instructions:`, instructions);
  }
  return lines;
}

export function specificEmailRecipient(channel: {
  type?: TrackEscalationMethod;
  recipientType?: TrackEscalationRecipientType | null;
  recipientEmail?: string | null;
  recipient?: string | null;
}):
  | {
      recipientType: TrackEscalationRecipientType.EMAIL;
      recipientEmail: string;
    }
  | undefined {
  if (channel.type && channel.type !== TrackEscalationMethod.EMAIL) {
    return undefined;
  }
  if (
    channel.recipientType === TrackEscalationRecipientType.RESPONSIBLE_PARTY
  ) {
    return undefined;
  }
  const recipientEmail =
    trimmedEmail(channel.recipientEmail) ||
    leftoverRecipientEmail(channel.recipient);
  if (!recipientEmail) {
    return undefined;
  }
  return {
    recipientType: TrackEscalationRecipientType.EMAIL,
    recipientEmail,
  };
}

function emailRecipientLabel(channel: TrackEscalationChannel): string {
  return (
    specificEmailRecipient(channel)?.recipientEmail ?? 'the responsible party'
  );
}

function leftoverRecipientEmail(recipient?: string | null): string {
  const trimmed = recipient?.trim() ?? '';
  return trimmed.includes('@') ? trimmed : '';
}

function trimmedEmail(value?: string | null): string {
  return value?.trim() ?? '';
}

function createTrackEscalationRuleId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `escalation-${Date.now()}`;
}
